/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApplePayProcessorType } from 'hooks/apple-pay/useApplePay';
import { Checkbox, SubmitButton, Typography } from 'omakase';
import { DoubleBooking } from 'components/business/checkout/DoubleBookingWarning';
import { IWithContextProps, consumer, core, withContext } from '@tock/coretypes';
import { LoadingContainer } from 'ui';
import { NewCardPaymentElement, NewCardPaymentElementData } from '@tock/payment-elements';
import { PaymentMethodForm } from 'forms/payment-method/PaymentMethodForm';
import { PurchaseState } from 'state/purchase';
import {
  TRY_ANOTHER_METHOD_ERROR_MESSAGE,
  convertToApplePayProcessingType,
  getApplePayErrorMessage,
} from 'utils/applePay';

import {
  ACTION_BUSINESS_CLICK_CHECKOUT_COMPLETE_PURCHASE,
  ACTION_CHECKOUT_VIEW_PAYMENT_FIELDS,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import { SmsSubscribeConfirmationModal } from 'components/modal/SmsSubscribeConfirmationModal';
import { WithApplePayProps, withApplePay } from 'utils/withApplePay';
import { amplitudeSendEvent } from 'utils/analytics/amplitude';
import { cardExpiryMatchesPattern, isExpiryAfterServiceDate } from 'utils/creditCardUtils';
import { find, isEmpty, some, sumBy } from 'es-toolkit/compat';
import { getAmountDue, getHoldAmountDue } from '@tock/shared/utils/purchase';
import { getBusinessPossessiveName } from 'utils/businessUtils';
import { getFullName } from '@tock/shared/utils/patron';
import { getMultiCheckoutProgress } from 'utils/lock';
import { isEmail, validateFirstLastNameIfPresent } from '@tock/shared/utils/FormValidators';
import {
  isLegacyUnsubscribedFromSms,
  isSmsPhoneStatusTypeOptIn,
  isSmsPhoneStatusTypeOptOutHard,
} from 'utils/smsUtils';
import { loadingContainer } from 'forms/accept-terms/acceptTermsFormCssDefs';
import { reduxForm } from 'redux-form';
import { shouldUsePaidQuickCheckout } from 'utils/quickCheckoutUtils';
import AcceptTermsCheckboxes from 'forms/accept-terms/AcceptTermsCheckboxes';
import Anchor from 'components/Anchor';
import ApplePayButton from 'forms/payment-method/apple-pay/ApplePayButton';
import BaseComponent from '@tock/shared/components/BaseComponent';
import ButtonGroup from '@tock/shared/components/buttons/ButtonGroup';
import CustomCancellationPolicyCard from 'components/business/checkout/CustomCancellationPolicyCard';
import FeatureFlag from '@tock/shared/utils/featureFlagContext';
import Field from '@tock/shared/components/forms/Field';
import Form from '@tock/shared/components/forms/Form';
import GuestInformation from 'components/business/quick-checkout/GuestInformation';
import ResyCreditBanner from 'components/business/landing-page/ResyCreditBanner';
import TermsLabel from 'forms/accept-terms/TermsLabel';
import TextInput from '@tock/shared/components/inputs/DeprecatedTextInput';
import Textarea from '@tock/shared/components/inputs/Textarea';
import TurnstileValidator, {
  TokenRefreshHandle,
  isTurnstileActive,
} from 'forms/TurnstileValidator';
import connect, { ActionsType, DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';

interface State {
  isSmsConfirmationModalOpen: boolean;
  unauthenticatedPhoneConsentStatus?: core.SmsPhone.StatusType;
}

export interface AcceptTermsFormFields {
  acceptsConciergeTerms: boolean;
  acceptsCreditCardHoldTerms: boolean;
  amountToPayUsingPoints: number;
  consentsToSMS: boolean;
  cvc: string;
  expiry: string;
  name: string;
  number: string;
  optsInToEmail: boolean;
  orderNotes: string;
  hasVerifiedAge: boolean;
  patron: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    phoneCountryCode: string;
    smsPhoneConsentStatus: core.SmsPhone.StatusType | undefined;
    zipCode: string;
  };
  zipCode: string;
  skipAvsCheck: boolean;
  paymentMethod: PaymentMethods;
  availablePaymentMethods?: PaymentMethods[];
  isSupportedApplePayEnvironment: boolean;
  nonceToken?: string;
  storedToken?: string;
}

type PaymentMethods = keyof typeof PAYMENT_METHODS;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const enum PAYMENT_METHODS {
  APPLE_PAY = 'APPLE_PAY',
  CREDIT_CARD = 'CREDIT_CARD',
}

export interface CommunicationOptInSettings {
  showControl: boolean;
  isInitiallyOptedIn: boolean;
  optInText: string;
}

export const DEFAULT_OPT_IN_TEXT = 'I would like to receive news and updates from this business.';

export const DEFAULT_OPT_IN_TEXT_CASL =
  'I consent to share contact and guest information about myself or someone who provided consent, for the business to send marketing communications about offers, promotions or other information. I can withdraw consent at any time directly with the business.';
interface Props {
  actions?: ActionsType;
  /**
   * Controls the appearance and behavior of the communications opt-in
   * checkbox field. If it is undefined, the form should not display
   * this information or collect any changes to the related setting. */
  communicationsOptInSettings: CommunicationOptInSettings;
  basePurchase?: core.PastPurchase;
  business?: consumer.IBusinessConfig;
  communicationPolicy?: core.ICommunicationPolicy;
  detectedLocation: core.DetectedLocation;
  doubleBooking?: DoubleBooking;
  experiments?: StoreType['experiments'];
  guest?: core.Patron;
  hasExternalGiftCardPayment?: boolean;
  hasLoyaltyPointsPayment?: boolean;
  hasValidCard?: boolean;
  isAuthenticated?: boolean;
  isFreeBooking: boolean;
  isConciergeBookViaEmail: boolean;
  isInQuickCheckout: boolean;
  isTableSideOrder: boolean;
  isTableSideOrderPayment: boolean;
  lastGuest?: StoreType['lastGuest'];
  lock: StoreType['lock'];
  newCardPaymentElementData?: NewCardPaymentElementData;
  onPurchase(formValues?: AcceptTermsFormFields): Promise<unknown>;
  onSelectCard(card?: core.PaymentCard): void;
  onTurnstileUpdate?: (turnstileResponse?: string) => void;
  purchase: StoreType['purchase'];
  requiredAge?: number;
  selectedExperience?: core.TicketType;
  shouldSaveCreditCard: boolean;
  showConciergeTOS: boolean; // if owner is not the same as diner, or during concierge book via email flow
  isConcierge?: boolean; // if owner is not the same as diner
  totalPrice: number;
  applePayProcessorType?: ApplePayProcessorType;
  isCreditCardHold?: boolean;
  showPayment: boolean;
  amountCents: number;
  countryCode?: string;
  currencyCode?: string;
  turnstileValidatorRef?: React.RefObject<TokenRefreshHandle>;
  sessionToken?: string;
  isInstrumentationEnabled?: boolean;
  shouldAskForSmsConsent: boolean;
}

interface ConnectProps extends DefaultConnectProps {
  paymentCard: StoreType['paymentCard'];
  selection: StoreType['selection'];
  rescheduleRequestInProgress: boolean;
  dispatch?: any;
  change?: (field: string, value: any) => void;
}

type AllProps = Props &
  ConnectProps &
  ReduxFormProps<AcceptTermsFormFields> &
  IWithContextProps &
  WithApplePayProps;

const requireFields =
  (...names: { field: string; name: string }[]) =>
  (data: any) =>
    names.reduce((errors: any, field) => {
      if (!data[field.field]) {
        errors[field.field] = field.name + ' is required';
      }
      return errors;
    }, {});

const validateEmail = (email: string, errors: any) => {
  if (!isEmail(email)) {
    errors.patron.email = 'Please enter a valid email';
  }
};

const validatePatron = requireFields(
  { field: 'firstName', name: 'First name' },
  { field: 'lastName', name: 'Last name' },
  { field: 'email', name: 'Email address' },
  { field: 'phone', name: 'Phone' },
  { field: 'phoneCountryCode', name: 'Phone' }
);

const isCCHoldRequiringMoney = (
  ticketPriceInformation: core.ITicketPriceInformation | null | undefined
) =>
  ticketPriceInformation?.priceType === 'PAYMENT_CARD_HOLD' &&
  (ticketPriceInformation?.amountCents ?? 0) > 0;

const validate = (
  values: AcceptTermsFormFields,
  props: Props & ConnectProps & IWithContextProps
) => {
  const { isInQuickCheckout, isAuthenticated, showConciergeTOS, requiredAge, purchase } = props;
  const errors: ReduxFormErrors<AcceptTermsFormFields> = {};
  if (isInQuickCheckout && !isAuthenticated) {
    errors.patron = validatePatron(values.patron);
    validateEmail(values.patron.email, errors);
    const nameErrors = validateFirstLastNameIfPresent(values.patron);
    if (!isEmpty(nameErrors)) {
      errors.patron = { ...errors.patron, ...nameErrors };
    }
  }
  if (showConciergeTOS && !values.acceptsConciergeTerms) {
    errors.acceptsConciergeTerms =
      'To book on behalf of a guest, you must accept the concierge agreement';
  }

  const purchaseOrder = purchase.calculatedPrice;
  const isPaidCCHold = isCCHoldRequiringMoney(purchaseOrder?.ticketPriceInformation);
  if (isPaidCCHold && !values.acceptsCreditCardHoldTerms) {
    errors.acceptsCreditCardHoldTerms =
      'Accept the cancellation policy terms to complete your booking';
  }

  if (isPaidCCHold && values.expiry && cardExpiryMatchesPattern(values.expiry)) {
    const expiryAfterReservation = isExpiryAfterServiceDate(
      (purchaseOrder as core.PurchaseOrder).dateTime,
      values.expiry
    );

    if (!expiryAfterReservation) {
      errors.expiry = 'Your card expires before date of your reservation';
    }
  }

  if (requiredAge && !values.hasVerifiedAge) {
    errors.hasVerifiedAge = 'You must verify you meet the age requirements to complete this order.';
  }
  return errors;
};

export const getPaymentDetails = (
  purchaseState: PurchaseState,
  isConciergeBookViaEmail: boolean
): { showPayment: boolean; amountCents: number } => {
  const purchaseOrder = purchaseState.calculatedPrice as core.PurchaseOrder;

  const amountCents = purchaseState.calculatedPackagePrice
    ? sumBy(purchaseState.calculatedPackagePrice.purchaseOrder, 'payNowCents')
    : getAmountDue(purchaseOrder);
  const holdCents = getHoldAmountDue(purchaseOrder);

  return {
    amountCents,
    showPayment: !isConciergeBookViaEmail && (amountCents > 0 || holdCents > 0),
  };
};

const getPreviousPaymentMethod = (purchase?: core.IPastPurchase): PaymentMethods | undefined => {
  if (purchase?.usedApplePay) {
    return 'APPLE_PAY';
  }
  if ((purchase?.paymentCardPaidCents ?? 0) > 0) {
    return 'CREDIT_CARD';
  }
  return undefined;
};

const getDefaultPaymentMethod = (
  isApplePaySupported: boolean | undefined,
  hasCardInAppleWallet: boolean | undefined,
  previousPaymentMethod: PaymentMethods | undefined
): PaymentMethods => {
  const preferredPaymentMethod =
    previousPaymentMethod ?? (hasCardInAppleWallet ? 'APPLE_PAY' : 'CREDIT_CARD');

  if (isApplePaySupported && preferredPaymentMethod === 'APPLE_PAY') {
    return 'APPLE_PAY';
  }

  return 'CREDIT_CARD';
};

export const hasRequiredApplePayFields = (
  processingType: core.PaymentProcessorType | undefined | null,
  values: AcceptTermsFormFields
): boolean => {
  const applePayProcessorType = convertToApplePayProcessingType(processingType);
  return (
    !!applePayProcessorType &&
    ((applePayProcessorType === 'BRAINTREE' && !!values.nonceToken && !!values.zipCode) ||
      (applePayProcessorType === 'STRIPE' && !!values.storedToken))
  );
};

class AcceptTermsForm extends BaseComponent<AllProps, State> {
  submitInProgress = false;
  state: State = { isSmsConfirmationModalOpen: false };

  constructor(props: AllProps) {
    super(props);
  }

  handleValidatePhone = (
    _isPhoneValid: boolean,
    smsPhoneConsentStatus: core.SmsPhone.StatusType
  ) => {
    this.setState({ unauthenticatedPhoneConsentStatus: smsPhoneConsentStatus });
  };

  getButtonText() {
    const {
      isConciergeBookViaEmail,
      isTableSideOrder,
      isTableSideOrderPayment,
      basePurchase,
      totalPrice,
    } = this.props;
    if (basePurchase) {
      return 'Confirm modified reservation';
    } else if (isTableSideOrder && !isTableSideOrderPayment) {
      return 'Submit order and pay';
    } else if (isTableSideOrder && isTableSideOrderPayment) {
      return 'Complete payment';
    } else if (isConciergeBookViaEmail) {
      return 'Send concierge email reservation invitation';
    } else if (totalPrice > 0) {
      return 'Complete purchase';
    } else {
      return 'Complete reservation';
    }
  }

  getButtonTextSuffix() {
    const { lock } = this.props;

    const { totalItems, currentItemIndex, isMultiCheckout } = getMultiCheckoutProgress(lock);

    if (isMultiCheckout) {
      return ` ${currentItemIndex}/${totalItems}`;
    }
    return '';
  }

  handleSelectCard(newSelectedCard: { value: any }) {
    const { paymentCard, onSelectCard } = this.props;
    const card = find(paymentCard.cards, { storedToken: newSelectedCard.value });
    onSelectCard(card as core.PaymentCard);
  }

  submit(formValues: AcceptTermsFormFields) {
    this.submitInProgress = true;
    return this.props
      .onPurchase(formValues)
      .then(() => {
        this.submitInProgress = false;
      })
      .catch((rejection) => {
        this.submitInProgress = false;
        return Promise.reject(rejection);
      });
  }

  // TODO, where is total price comeing from in this flow, should it be using getAmountDue util?
  shouldRevalidateSavedCreditCards() {
    const {
      app: { config, settings },
      features,
      business: businessConfig,
      totalPrice,
      purchase,
    } = this.props;
    const business = config?.business ?? businessConfig;

    const isBraintree = business?.paymentProcessorType === 'BRAINTREE';
    const isHoldAndShouldRequireForHold =
      (features.BRAINTREE_CONSUMER_CC_HOLD_VERIFICATION ?? false) &&
      purchase.calculatedPrice?.ticketPriceInformation?.priceType === 'PAYMENT_CARD_HOLD';
    const amountDue = (totalPrice ?? 0) - (purchase.calculatedPrice?.netAmountPaidCents ?? 0);

    return (
      isBraintree &&
      (amountDue > settings.REVALIDATE_THRESHOLD_CENTS || isHoldAndShouldRequireForHold)
    );
  }

  renderCreditCardForm(isSubmitting: boolean) {
    const {
      context: { isFeatureEnabled },
      fields,
    } = this.props;
    // Show billing fields if the billing address field is enabled and the user is in the test group of the PaymentFieldBillingAddress experiment
    const showBillingFields =
      isFeatureEnabled('STRIPE_AMEX_FULL_BILLING_ADDRESS') &&
      (!isFeatureEnabled('CBAC_CONCIERGE_DISABLE_CONSUMER') || this.props.isConcierge === false);
    return (
      <div className="ConsumerCheckout-addCardForm">
        {!showBillingFields && (
          <Field label={'Name'} labelFor={'name'} field={fields.name}>
            <TextInput
              id="name"
              autoComplete="off"
              autoCorrect="off"
              disabled={isSubmitting}
              field={fields.name}
              placeholder={'Name on card'}
              data-testid="credit-card-name-input"
            />
          </Field>
        )}
        <NewCardPaymentElement
          callSite="consumer_checkout"
          showAddressFields={showBillingFields}
          paymentElementOnReady={() => {
            amplitudeSendEvent(ACTION_CHECKOUT_VIEW_PAYMENT_FIELDS);
          }}
        />
      </div>
    );
  }

  handleApplePaySubmit({ nonceToken, storedToken, zipCode }: core.IPaymentCard): Promise<unknown> {
    const { values, handleSubmit } = this.props;

    return new Promise<void>((resolve, reject) => {
      const submitApplePay = () =>
        this.submit({
          ...values,
          zipCode: zipCode ?? '',
          nonceToken: nonceToken ?? undefined,
          storedToken: storedToken ?? undefined,
        })
          .then(resolve)
          .catch(reject);

      handleSubmit(submitApplePay)();
    });
  }

  handleApplePayButtonClick() {
    const { actions, startApplePaySession } = this.props;

    if (startApplePaySession) {
      return startApplePaySession(this.handleApplePaySubmit).catch((reason) => {
        const errorMessage = getApplePayErrorMessage(reason);
        errorMessage && actions.toast.showError(errorMessage);
      });
    } else {
      actions.toast.showError(TRY_ANOTHER_METHOD_ERROR_MESSAGE);
      return Promise.reject();
    }
  }

  maybeShowSmsConfirmationModal = (
    e: React.MouseEvent<HTMLButtonElement>,
    currentSmsPhoneConsentStatus: core.SmsPhone.StatusType | undefined
  ) => {
    const shouldSkipSmsConfirmationModal = isSmsPhoneStatusTypeOptIn(currentSmsPhoneConsentStatus);

    if (shouldSkipSmsConfirmationModal) {
      return;
    }

    // Interrupt form submission only when SMS confirmation is required.
    e.preventDefault();
    // Open the modal
    this.setState({ isSmsConfirmationModalOpen: true });
  };

  render() {
    const {
      actions,
      app: { config, jwtToken },
      business,
      communicationPolicy,
      error,
      fields,
      handleSubmit,
      hasExternalGiftCardPayment,
      hasLoyaltyPointsPayment,
      invalid,
      isAuthenticated,
      isConciergeBookViaEmail,
      isFreeBooking,
      isInQuickCheckout,
      paymentCard: { insertRequestInProgress, isInitialized, tokenizeRequestInProgress },
      purchase,
      selectedExperience,
      shouldSaveCreditCard,
      submitting,
      doubleBooking,
      rescheduleRequestInProgress,
      isApplePaySupported,
      isApplePayLoading,
      isTableSideOrder,
      requiredAge,
      communicationsOptInSettings,
      showPayment,
      sessionToken,
      isInstrumentationEnabled,
      context: { isFeatureEnabled },
      shouldAskForSmsConsent,
    } = this.props;
    const businessConfig = config?.business ?? business;
    const usePaidQuickCheckout = purchase.calculatedPrice
      ? shouldUsePaidQuickCheckout(
          purchase.calculatedPrice,
          jwtToken,
          isFeatureEnabled('PAID_QUICK_CHECKOUT')
        )
      : false;
    const cards = usePaidQuickCheckout ? [] : this.props.paymentCard?.cards;
    const selectedCard = this.props.selection.card;
    const {
      acceptsConciergeTerms,
      acceptsCreditCardHoldTerms,
      consentsToSMS,
      hasVerifiedAge,
      optsInToEmail,
    } = fields;

    const isSubmitting =
      submitting ||
      tokenizeRequestInProgress ||
      insertRequestInProgress ||
      purchase.requestInProgress ||
      rescheduleRequestInProgress ||
      this.submitInProgress;

    const disableButton =
      isSubmitting ||
      invalid ||
      doubleBooking?.restrictBooking ||
      !!acceptsCreditCardHoldTerms.error;

    const wwwUrl = `https://www.exploretock.com`;

    const conciergeTermsUrl = `${wwwUrl}/concierge-terms`;

    const purchaseOrder = purchase.calculatedPrice as core.PurchaseOrder;

    const ticketPriceInformation = purchaseOrder.ticketPriceInformation || undefined;

    const conciergeTerms = isConciergeBookViaEmail
      ? 'send a concierge email reservation invitation to this guest'
      : 'make a reservation on behalf of this guest';
    const acceptsConciergeLabel = (
      <span>
        I have read and acknowledged the{' '}
        <Anchor href={conciergeTermsUrl} target="_blank" className="u-activeColor">
          Concierge Agreement
        </Anchor>{' '}
        to {conciergeTerms}.
      </span>
    );

    const showOrderNotes =
      selectedExperience?.variety === 'ORDER_DELIVERY' ||
      selectedExperience?.variety === 'ORDER_PICKUP';

    const shouldRevalidateSavedCreditCards = this.shouldRevalidateSavedCreditCards();

    const isTurnstileFlagEnabled = isFeatureEnabled('TURNSTILE_RENDER_CLIENT');
    const includeTurnstileValidation =
      isTurnstileActive(isTurnstileFlagEnabled) && usePaidQuickCheckout;

    const doAskForConsent =
      isFeatureEnabled('TWILIO_DIRECT_BRAND_UPDATES') &&
      isFeatureEnabled('TWILIO_DIRECT_BRAND_DATA_ONLY') &&
      !this.props.isConcierge &&
      shouldAskForSmsConsent;
    const currentSmsPhoneConsentStatus = this.props.guest
      ? this.props.guest.smsPhoneConsentStatus
      : this.state.unauthenticatedPhoneConsentStatus;
    const phoneIsHardOptOut = isSmsPhoneStatusTypeOptOutHard(currentSmsPhoneConsentStatus);

    return (
      <Form
        modifierClassName="Form--default Form--no-padding"
        onSubmit={handleSubmit(this.submit)}
        data-testid="credit-card-form"
      >
        {isInQuickCheckout && !isAuthenticated && (
          <GuestInformation
            isSubmitting={isSubmitting}
            fields={this.props.fields}
            onValidatePhone={this.handleValidatePhone}
          />
        )}
        {showOrderNotes && (
          <FeatureFlag ifEnabled="ORDER_NOTES">
            <div className="ConsumerCheckout-section">
              <div className="ConsumerCheckout-sectionHeading">
                <Typography component="h2" variant="body02Strong">
                  Add order notes
                </Typography>
              </div>
              <div>
                <Field label="Order notes" labelFor="orderNotes">
                  <Textarea
                    field={fields.orderNotes}
                    id="orderNotes"
                    placeholder="Please enter any details or special requests for your order"
                  />
                </Field>
              </div>
            </div>
          </FeatureFlag>
        )}
        <LoadingContainer loading={showPayment && !!isApplePayLoading} css={loadingContainer}>
          {showPayment && (
            <>
              <PaymentMethodForm
                actions={actions}
                cards={cards}
                creditCardFieldHasError={some(fields, (field: any) => !!field.error)}
                cvcField={fields.cvc}
                error={error}
                isInitialized={isInitialized || usePaidQuickCheckout}
                isSubmitting={isSubmitting}
                onRenderCreditCardForm={this.renderCreditCardForm}
                onSelectCard={this.handleSelectCard}
                purchase={purchase}
                selectedCard={selectedCard}
                shouldSaveCreditCard={shouldSaveCreditCard}
                shouldRevalidateSavedCreditCards={shouldRevalidateSavedCreditCards}
                paymentMethod={fields.paymentMethod}
                isApplePaySupported={isApplePaySupported}
              />
              {includeTurnstileValidation && (
                <TurnstileValidator
                  onReceiveChallengeResponse={(challengeResponse) => {
                    if (this.props.onTurnstileUpdate) {
                      this.props.onTurnstileUpdate(challengeResponse);
                    }
                  }}
                  ref={this.props.turnstileValidatorRef}
                />
              )}
            </>
          )}

          <AcceptTermsCheckboxes
            isTableSideOrder={isTableSideOrder}
            selectedExperience={selectedExperience}
            businessName={business?.name || undefined}
            consentsToSMS={consentsToSMS}
            hasVerifiedAge={hasVerifiedAge}
            optsInToEmail={optsInToEmail}
            requiredAge={requiredAge}
            communicationsOptInSettings={communicationsOptInSettings}
          />

          {!this.props.isTableSideOrder && (
            <CustomCancellationPolicyCard
              businessName={businessConfig?.name ?? ''}
              communicationPolicy={communicationPolicy}
              isFreeBooking={isFreeBooking}
              hasExternalGiftCardPayment={hasExternalGiftCardPayment}
              hasLoyaltyPointsPayment={hasLoyaltyPointsPayment}
              ticketPriceInformation={ticketPriceInformation}
            />
          )}
          {this.props.showConciergeTOS && (
            <div className="ConsumerCheckout-section">
              <Field field={acceptsConciergeTerms}>
                <Checkbox
                  checked={acceptsConciergeTerms.value}
                  onChange={acceptsConciergeTerms.onChange}
                  value={acceptsConciergeTerms.value}
                  id="acceptsConcierge"
                  label={acceptsConciergeLabel}
                  weight="book"
                  data-testid="accept-concierge-check"
                />
              </Field>
            </div>
          )}
          {isCCHoldRequiringMoney(ticketPriceInformation) && (
            <div className="ConsumerCheckout-section">
              <Field field={acceptsCreditCardHoldTerms}>
                <Checkbox
                  checked={acceptsCreditCardHoldTerms.value}
                  onChange={acceptsCreditCardHoldTerms.onChange}
                  value={acceptsCreditCardHoldTerms.value}
                  id="acceptsConcierge"
                  label={`I have read and acknowledge ${getBusinessPossessiveName(
                    business?.name || ''
                  )} cancellation policy.`}
                  weight="book"
                  data-testid="accept-cancel-policy-check"
                />
              </Field>
            </div>
          )}
          {business?.isResyCreditEligible && (
            <div className="ConsumerCheckout-section">
              <ResyCreditBanner titleVariant="body02Strong" titleMarginBottom={0} />
            </div>
          )}
          <div className="ConsumerCheckout-section">
            {doAskForConsent ? (
              <ButtonGroup>
                {fields.paymentMethod.value === 'APPLE_PAY' && showPayment ? (
                  <>
                    {/* TODO: TK-26896 Make apple pay support sms confirmation modal flow */}
                    <ApplePayButton onClick={this.handleApplePayButtonClick} />
                  </>
                ) : (
                  <SubmitButton
                    type="submit"
                    variant="primary"
                    disabled={disableButton}
                    fullWidth
                    isSubmitting={isSubmitting}
                    size="large"
                    data-testid="purchase-button"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                      if (isInstrumentationEnabled && sessionToken) {
                        amplitudeSendEvent(ACTION_BUSINESS_CLICK_CHECKOUT_COMPLETE_PURCHASE, {
                          businessId: businessConfig?.id ?? undefined,
                          sessionToken,
                        });
                      }

                      this.maybeShowSmsConfirmationModal(event, currentSmsPhoneConsentStatus);
                    }}
                  >
                    {this.getButtonText()}
                    {this.getButtonTextSuffix()}
                  </SubmitButton>
                )}
              </ButtonGroup>
            ) : (
              <ButtonGroup>
                {fields.paymentMethod.value === 'APPLE_PAY' && showPayment ? (
                  <ApplePayButton onClick={this.handleApplePayButtonClick} />
                ) : (
                  <SubmitButton
                    variant="primary"
                    disabled={disableButton}
                    fullWidth
                    isSubmitting={isSubmitting}
                    size="large"
                    data-testid="purchase-button"
                    onClick={() => {
                      if (isInstrumentationEnabled && sessionToken) {
                        amplitudeSendEvent(ACTION_BUSINESS_CLICK_CHECKOUT_COMPLETE_PURCHASE, {
                          businessId: businessConfig?.id ?? undefined,
                          sessionToken,
                        });
                      }
                    }}
                  >
                    {this.getButtonText()}
                    {this.getButtonTextSuffix()}
                  </SubmitButton>
                )}
              </ButtonGroup>
            )}
          </div>

          {shouldAskForSmsConsent && (
            <SmsSubscribeConfirmationModal
              curPatronHardUnsubscribed={phoneIsHardOptOut}
              isOpen={this.state.isSmsConfirmationModalOpen}
              onClose={(chosenStatus: core.SmsPhone.StatusType) => {
                this.setState({ isSmsConfirmationModalOpen: false });

                if (purchaseOrder?.patron) {
                  purchaseOrder.patron.smsPhoneConsentStatus = chosenStatus;
                }
                this.props.change?.('patron.smsPhoneConsentStatus', chosenStatus);
                this.props.handleSubmit((values) =>
                  this.submit({
                    ...values,
                    patron: {
                      ...values.patron,
                      smsPhoneConsentStatus: chosenStatus,
                    },
                  })
                )();
              }}
              pageType="Checkout"
            />
          )}

          <div className="ConsumerCheckout-section">
            <div className="ConsumerCheckout-acceptTerms">
              <Typography component="span" variant="caption">
                <TermsLabel
                  businessConfig={business}
                  isTableside={!!purchaseOrder?.visitEditToken}
                />
              </Typography>
            </div>
          </div>
        </LoadingContainer>
      </Form>
    );
  }
}

const acceptTermsForm = reduxForm<
  Props & ConnectProps & IWithContextProps & WithApplePayProps,
  AcceptTermsFormFields
>(
  {
    form: 'AcceptTermsForm',
    fields: [
      'acceptsConciergeTerms',
      'acceptsCreditCardHoldTerms',
      'amountToPayUsingPoints',
      'consentsToSMS',
      'cvc',
      'expiry',
      'hasVerifiedAge',
      'optsInToEmail',
      'orderNotes',
      'name',
      'number',
      'patron.id',
      'patron.email',
      'patron.firstName',
      'patron.lastName',
      'patron.phone',
      'patron.phoneCountryCode',
      'patron.smsPhoneConsentStatus',
      'patron.zipCode',
      'skipAvsCheck',
      'zipCode',
      'paymentMethod',
      'availablePaymentMethods',
      'isSupportedApplePayEnvironment',
    ],
    validate,
  },
  (state, props: Props & ConnectProps & IWithContextProps & WithApplePayProps) => {
    const {
      detectedLocation,
      guest,
      communicationsOptInSettings: { isInitiallyOptedIn },
      lastGuest,
      isApplePaySupported,
      isSupportedApplePayEnvironment,
      hasCardInAppleWallet,
      showPayment,
      basePurchase,
    } = props;

    const hasConsentedToSms = guest
      ? !isLegacyUnsubscribedFromSms(guest) ||
        isSmsPhoneStatusTypeOptIn(guest?.smsPhoneConsentStatus)
      : false;

    return {
      initialValues: {
        name: getFullName(guest) || '',
        consentsToSMS: hasConsentedToSms,
        cvc: '',
        expiry: '',
        optsInToEmail: isInitiallyOptedIn,
        number: '',
        orderNotes: '',
        hasVerifiedAge: false,
        zipCode: '',
        patron: {
          id: null,
          firstName: lastGuest?.guest?.firstName || '',
          lastName: lastGuest?.guest?.lastName || '',
          email: lastGuest?.guest?.email || '',
          phone: lastGuest?.guest?.phone || '',
          phoneCountryCode:
            guest?.phoneCountryCode || detectedLocation?.phoneCountryCode || '+1 US',
          smsPhoneConsentStatus: guest?.smsPhoneConsentStatus,
          zipCode: '',
        },
        paymentMethod: showPayment
          ? getDefaultPaymentMethod(
              isApplePaySupported,
              hasCardInAppleWallet,
              getPreviousPaymentMethod(basePurchase)
            )
          : undefined,
        availablePaymentMethods: showPayment
          ? isApplePaySupported
            ? ['APPLE_PAY', 'CREDIT_CARD']
            : ['CREDIT_CARD']
          : undefined,
        isSupportedApplePayEnvironment: isSupportedApplePayEnvironment,
      },
    };
  }
)(AcceptTermsForm);

const mapStateToProps = (state: StoreType) => {
  const { experiments, paymentCard, selection, reschedule, app } = state;
  return {
    experiments,
    paymentCard,
    selection,
    rescheduleRequestInProgress: reschedule.requestInProgress,
    sessionToken: app.sessionToken,
    isInstrumentationEnabled:
      state.features.features['CONSUMER_VENUE_PAGE_CONVERSION_INSTRUMENTATION'] || false,
  };
};

export default connect<Props & WithApplePayProps, ConnectProps>(
  withContext(withApplePay(acceptTermsForm)),
  mapStateToProps
);
